<template>
  <div class="dock-btn-wrap" :class="'dock-pos-' + dockShared.position">
    <button
      type="button"
      class="dock-btn"
      :class="{ 'dock-btn-active': isOpen || active }"
      :aria-label="label"
      :title="label"
      @click="onClick"
    >
      <span class="dock-btn-icon"><slot name="icon"></slot></span>
      <span class="dock-btn-label">{{ label }}</span>
    </button>
    <transition name="dock-pop">
      <div
        v-if="expandable && isOpen"
        class="dock-popover"
        :class="'popover-' + dockShared.position"
        @click.stop
      >
        <div class="dock-popover-title">{{ label }}</div>
        <div class="dock-popover-body">
          <slot></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
let uid = 0;

export default {
  name: 'DockButton',
  inject: ['dockShared'],
  props: {
    label: { type: String, required: true },
    expandable: { type: Boolean, default: true },
    active: { type: Boolean, default: false }
  },
  data() {
    return {
      id: 'dock-btn-' + uid++
    };
  },
  computed: {
    isOpen() {
      return this.expandable && this.dockShared.activeId === this.id;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick, true);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick, true);
  },
  methods: {
    onClick() {
      if (!this.expandable) {
        this.$emit('trigger');
        return;
      }
      this.dockShared.activeId = this.isOpen ? null : this.id;
    },
    handleOutsideClick(e) {
      if (this.isOpen && this.$el && !this.$el.contains(e.target)) {
        this.dockShared.activeId = null;
      }
    }
  }
};
</script>

<style scoped>
.dock-btn-wrap {
  position: relative;
}

.dock-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  min-width: 56px;
  padding: 0.45rem 0.6rem;
  border: none;
  border-radius: 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.1s ease;
  font-family: var(--ff-body, 'Inter', sans-serif);
}
.dock-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  transform: translateY(-1px);
}
.dock-btn-active {
  background: var(--c-primary, #2B6960);
  color: white;
}
.dock-btn-active:hover {
  background: var(--c-primary-light, #3A8A7E);
}

.dock-btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}
.dock-btn-label {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

/* On side docks, keep the bar compact - icon only, label becomes a tooltip */
.dock-pos-left .dock-btn-label,
.dock-pos-right .dock-btn-label {
  display: none;
}

/* ===== Popover ===== */
.dock-popover {
  position: absolute;
  background: rgba(30, 40, 38, 0.72);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 18px;
  padding: 1rem 1.1rem;
  min-width: 240px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);
  z-index: 50;
}
.popover-bottom {
  bottom: calc(100% + 16px);
  left: 50%;
  transform: translateX(-50%);
}
.popover-left {
  left: calc(100% + 16px);
  top: 50%;
  transform: translateY(-50%);
}
.popover-right {
  right: calc(100% + 16px);
  top: 50%;
  transform: translateY(-50%);
}

.dock-popover-title {
  font-family: var(--ff-heading, 'Playfair Display', serif);
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 0.65rem;
}
.dock-popover-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.dock-pop-enter-active,
.dock-pop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dock-pop-enter,
.dock-pop-leave-to {
  opacity: 0;
}
.popover-bottom.dock-pop-enter,
.popover-bottom.dock-pop-leave-to {
  transform: translateX(-50%) translateY(6px);
}
.popover-left.dock-pop-enter,
.popover-left.dock-pop-leave-to {
  transform: translateY(-50%) translateX(-6px);
}
.popover-right.dock-pop-enter,
.popover-right.dock-pop-leave-to {
  transform: translateY(-50%) translateX(6px);
}

@media (max-width: 600px) {
  .dock-popover {
    min-width: 200px;
  }
}
</style>
